import { useCallback, useRef } from "react";
import type { IngestStatus, LatestReading } from "@spf/contracts";
import { api } from "../lib/client.js";
import { useAsync } from "../lib/useAsync.js";
import { formatAge, formatDateTime, formatRelativeTime, formatUnit } from "../lib/format.js";
import { Empty, Failed, Loading } from "../components/ViewState.js";
import { DataList, DataRow, PageSection } from "../components/Page.js";
import { Freshness } from "../components/Freshness.js";
import { useFreshness } from "../lib/useFreshness.js";

/**
 * The latest reading and whether it can be believed. The panels' row joined
 * in slice 5c, once their fields were verified (docs/DECISIONS/0028).
 *
 * The two panels stay in this order. One test reads
 * `getAllByRole("status")[0]` to reach the reading's loader and
 * `getAllByRole("button", { name: "Try again" })[0]` to reach its retry, so
 * swapping the panels would silently point both at ingestion instead.
 */
export function TelemetryPage() {
  const latest = useAsync(
    api.latestReading,
    "Could not load the latest reading. The server may be unreachable.",
  );
  const status = useAsync(
    api.ingestStatus,
    "Could not load ingestion status. The server may be unreachable.",
  );
  const heading = useRef<HTMLDivElement>(null);

  const reload = useCallback(() => {
    latest.reload();
    status.reload();
    // Retrying unmounts the button that was just used, which drops keyboard
    // focus to the top of the document. The Live view has done this since
    // slice 4; it was left undone here, and is round 5's minor 2b.
    heading.current?.focus();
  }, [latest, status]);

  return (
    <div ref={heading} tabIndex={-1} className="flex flex-col gap-5 outline-none">
      <PageSection title="Telemetry">
        {latest.state.kind === "loading" && <Loading label="Loading the latest reading…" />}
        {latest.state.kind === "error" && (
          <Failed message={latest.state.message} onRetry={reload} />
        )}
        {latest.state.kind === "ready" && <Latest value={latest.state.value} />}
      </PageSection>

      <PageSection title="Ingestion">
        {status.state.kind === "loading" && <Loading label="Loading ingestion status…" />}
        {status.state.kind === "error" && (
          <Failed message={status.state.message} onRetry={reload} />
        )}
        {status.state.kind === "ready" && <Ingestion value={status.state.value} />}
      </PageSection>
    </div>
  );
}

function Latest({ value }: { value: LatestReading }) {
  const freshness = useFreshness(value);
  if (value.reading === null) {
    return (
      <Empty>
        No telemetry yet. Once the inverter&rsquo;s dongle reports, its readings appear here within
        about five minutes.
      </Empty>
    );
  }

  const { reading } = value;
  return (
    <div className="flex flex-col gap-4">
      <Freshness value={value} freshness={freshness} />
      <DataList>
        <DataRow label="Load">{formatUnit(reading.loadPowerW, "W")}</DataRow>
        <DataRow label="Panels">
          {formatUnit(reading.pvPowerW, "W")} at {formatUnit(reading.pvVoltageV, "V")}
        </DataRow>
        <DataRow label="Grid">
          {formatUnit(reading.gridPowerW, "W")} at {formatUnit(reading.gridVoltageV, "V")}
        </DataRow>
        <DataRow label="Battery">
          {reading.socPct}% charge, {formatUnit(reading.batteryVoltageV, "V")}
        </DataRow>
        {/* The sign is the inverter's own convention, carried through the API
            unchanged: negative means power going into the battery, and zero
            means neither, which used to read as "0 W discharging". */}
        <DataRow label="Battery power">{describeBatteryPower(reading.batteryPowerW)}</DataRow>
        <DataRow label="Inverter temperature">{formatUnit(reading.inverterTempC, "°C")}</DataRow>
        {/* The same clock as the Freshness line above. Reading this one off
            the browser and that one off the server's age gave the same
            reading two ages on any device whose clock is off. */}
        <DataRow label="Reading taken">
          {freshness.ageSeconds === null
            ? formatDateTime(reading.time)
            : `${formatAge(freshness.ageSeconds)} (${formatDateTime(reading.time)})`}
        </DataRow>
      </DataList>
      {reading.isBuffered && (
        <p className="text-sm text-ink-muted">
          This record was stored by the dongle during an outage and delivered late.
        </p>
      )}
    </div>
  );
}

/**
 * The battery row, shared in spirit with the live view's flow diagram: both
 * read the same signed field, and both have to say "idle" at zero rather than
 * claiming a direction the number does not have.
 */
function describeBatteryPower(watts: number): string {
  if (watts === 0) return "Idle";
  return `${formatUnit(Math.abs(watts), "W")} ${watts < 0 ? "charging" : "discharging"}`;
}

function Ingestion({ value }: { value: IngestStatus }) {
  if (value.lastFrameAt === null) {
    return <Empty>No frames have reached the ingest worker yet.</Empty>;
  }

  return (
    <DataList>
      <DataRow label="Last frame received">
        {formatRelativeTime(value.lastFrameAt)} ({formatDateTime(value.lastFrameAt)})
      </DataRow>
      <DataRow label="Frames stored">{formatUnit(value.framesIngested, "", true)}</DataRow>
      <DataRow label="Data records with no reading">
        {formatUnit(value.framesNoReading, "", true)}
      </DataRow>
      <DataRow label="Frames that failed to decode">
        {formatUnit(value.framesFailed, "", true)}
      </DataRow>
      <DataRow label="Frames from another datalogger">
        {formatUnit(value.framesRejected, "", true)}
      </DataRow>
      {value.collector !== null && (
        <>
          <DataRow label="Configure records blocked">
            {formatUnit(value.collector.commandsBlocked, "", true)}
          </DataRow>
          <DataRow label="Records with a bad checksum">
            {formatUnit(value.collector.crcFailures, "", true)}
          </DataRow>
        </>
      )}
    </DataList>
  );
}
